import { bindPromiseAction } from 'app-components/flux/core/actionHelpers';
import {
  reportConfigurationDefaultsSelectors,
  reportConfigurationsActions,
} from 'app-components/flux/report-configurations';
import type {
  OpenReportConfigurationPayload,
  ReportConfiguration,
  ReportNameType,
} from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { registerNavigationIntent } from './registry';
import type { NavigationIntent, NavigationResult } from './types';

type ReportIntentConfig = {
  name: string;
  description: string;
  reportNames?: ReportNameType[];
};

type ResolveReportNavigationArgs = {
  state: RootState;
  reportNames?: ReportNameType[];
  openReportConfiguration?: (payload: OpenReportConfigurationPayload) => Promise<ReportConfiguration | undefined>;
};

const MONTHLY_SUMMARY_PATH = '/reports/monthly-summary';

const reportIntentConfigs: ReportIntentConfig[] = [
  {
    name: 'report_spending',
    description: 'Spending report.',
    reportNames: ['total-spending'],
  },
  {
    name: 'report_income',
    description: 'Income report.',
    reportNames: ['income'],
  },
  {
    name: 'report_income_expense',
    description: 'Income & Expense report.',
    reportNames: ['income-expense'],
  },
  {
    name: 'report_savings',
    description: 'Savings report.',
    reportNames: ['savings'],
  },
  {
    name: 'report_net_worth',
    description: 'Net Worth report.',
    reportNames: ['net-worth'],
  },
  {
    name: 'report_monthly_summary',
    description: 'Monthly Summary report.',
  },
  {
    name: 'report_taxes',
    description: 'Tax reporting page.',
    reportNames: ['taxes', 'personal-taxes'],
  },
];

const getDefaultConfigForReportNames = (
  state: RootState,
  reportNames: readonly ReportNameType[],
): ReportConfiguration | undefined => {
  const defaultsByName = reportConfigurationDefaultsSelectors.getReportsConfigurationsByName(state);
  for (const reportName of reportNames) {
    const config = defaultsByName.get(reportName);
    if (config) {
      return config;
    }
  }
  return undefined;
};

const openReportConfigurationViaStore = async (
  payload: OpenReportConfigurationPayload,
): Promise<ReportConfiguration | undefined> => {
  if (!store) {
    return undefined;
  }

  const openReportConfiguration = bindPromiseAction<
    OpenReportConfigurationPayload,
    Record<string, never>,
    ReportConfiguration
  >(store.dispatch, reportConfigurationsActions.openReportConfiguration);

  return openReportConfiguration(payload);
};

export const resolveReportNavigation = async ({
  state,
  reportNames,
  openReportConfiguration = openReportConfigurationViaStore,
}: ResolveReportNavigationArgs): Promise<NavigationResult> => {
  if (!reportNames?.length) {
    return { type: 'navigate', payload: { path: MONTHLY_SUMMARY_PATH } };
  }

  const parentDefaultConfig = getDefaultConfigForReportNames(state, reportNames);
  if (!parentDefaultConfig?.id) {
    return {
      type: 'error',
      reason: `Unable to locate a default report configuration for ${reportNames.join(', ')}.`,
    };
  }

  try {
    const openedConfig = await openReportConfiguration({
      parentConfigId: parentDefaultConfig.id,
      parentPersistentType: parentDefaultConfig.persistentType,
    });

    if (!openedConfig?.id) {
      return {
        type: 'error',
        reason: `Unable to open a report tab for ${reportNames.join(', ')}.`,
      };
    }

    return {
      type: 'navigate',
      payload: { path: `/reports/${openedConfig.id}` },
    };
  } catch {
    return {
      type: 'error',
      reason: `Failed to open report tab for ${reportNames.join(', ')}.`,
    };
  }
};

for (const reportIntentConfig of reportIntentConfigs) {
  const intent: NavigationIntent<Record<string, never>, RootState> = {
    name: reportIntentConfig.name,
    description: reportIntentConfig.description,
    resolver: (_args, state) =>
      resolveReportNavigation({
        state,
        reportNames: reportIntentConfig.reportNames,
      }),
  };
  registerNavigationIntent(intent);
}
